/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2020, Zumper
 * @description  Image.tsx
 */

import React, { useEffect } from 'react'
import { useInView } from 'react-intersection-observer'

import { useRegisterCriticalAsset } from './useRegisterCriticalAsset'

export interface ImageProps {
  alt?: string
  /** The intrinsic height of the image, in pixels. Must be an integer without a unit. */
  height: number | string
  /** Indicates if the react app has finished hydrating. */
  hydrated?: boolean
  loading?: 'eager' | 'lazy'
  onChangeShouldUseSrc?: (should: boolean) => void
  onView?: () => void
  /** If set and `shouldPreload` is true, the image will only be preloaded when the given media query matches (same behavior as `Source`'s `media` prop) */
  preloadMedia?: string
  /** Indicates if this image should be preloaded via header link tag. Requires `srcSet` and `sizes` props to be defined. */
  shouldPreload?: boolean
  shouldUseSrc?: boolean
  sizes?: string | string[]
  src?: string
  srcSet?: string | string[]
  /** The intrinsic width of the image in pixels. Must be an integer without a unit. */
  width: number | string
}

export const stringifyProp = (
  prop?: string | string[],
  separator = ','
): string | undefined => {
  if (prop === undefined) {
    return prop
  }
  if (Array.isArray(prop)) {
    return prop.join(separator)
  }
  return prop
}

export const Image = ({
  alt = '',
  height,
  hydrated,
  loading = 'lazy',
  onChangeShouldUseSrc,
  onView,
  preloadMedia,
  shouldPreload,
  shouldUseSrc: shouldUseSrcProp,
  sizes,
  src,
  srcSet,
  width,
  ...otherProps
}: ImageProps &
  Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'sizes' | 'srcSet'>) => {
  const [ref, inView] = useInView({
    rootMargin: '200px 0px',
    triggerOnce: true,
  })

  const shouldUseSrc =
    typeof shouldUseSrcProp === 'undefined'
      ? loading !== 'lazy' ||
        (inView && (Boolean(hydrated) || hydrated === undefined))
      : shouldUseSrcProp

  const registerCriticalAsset = useRegisterCriticalAsset()
  const srcSetString = stringifyProp(srcSet)
  const sizesString = stringifyProp(sizes)

  if (shouldPreload && srcSetString) {
    registerCriticalAsset(`${srcSetString}${sizesString || ''}`, {
      as: 'image',
      imagesrcset: srcSetString,
      imagesizes: sizesString || undefined,
      media: preloadMedia || undefined,
    })
  }

  useEffect(() => {
    if (inView && onView) {
      onView()
    }
  }, [inView, onView])

  useEffect(() => {
    if (onChangeShouldUseSrc) {
      onChangeShouldUseSrc(shouldUseSrc)
    }
  }, [onChangeShouldUseSrc, shouldUseSrc])

  return (
    <img
      alt={alt}
      data-src={shouldUseSrc ? undefined : src}
      data-srcset={shouldUseSrc ? undefined : srcSetString}
      height={height}
      loading={loading}
      ref={ref}
      sizes={sizesString}
      // https://github.com/twobin/react-lazyload/issues/189#issuecomment-800546335
      // to fix images using srcSet downloading multiple images, had to change order or srcSet and src
      srcSet={shouldUseSrc ? srcSetString : undefined}
      src={shouldUseSrc ? src : undefined}
      width={width}
      {...otherProps}
    />
  )
}
